import React, { useEffect, useState, useCallback, useMemo, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { MdOutlineAdd, MdOutlineDelete, MdOutlineEdit, MdOutlineDownload, MdOutlineUpload, MdOutlineFileDownload, MdContentCopy } from "react-icons/md";
import { DataMgmtCatalogPagination } from "src/components/configuration/dataMgmtCatalogUi";
import { useNavigate } from "react-router";
import { Breadcrumb, BreadcrumbItem, Modal, Tooltip, UncontrolledTooltip } from "reactstrap";
import Button from "src/components/Buttons/Button";
import "./rcsa.css";
import "../DocumentManagement/DocumentManagement.css";
import "../controls/style.css";
import "../Surveys_Questions/Surveys/surveys.css";
import { useTranslation } from "react-i18next";
import { HiMagnifyingGlass } from "react-icons/hi2";
import { FilterIcon } from "lucide-react";
import Select from 'react-select';
import ExcelJS from 'exceljs';
import { toast } from "react-toastify";
import { getRCSAByPage, updateSurveyAssessmentApi } from "src/services/question";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { uploadExcelForRCSA } from "src/services/question";
import { getUserRolePermissions } from "src/services/userServicer";
import { getUserRole } from "src/reducers/userInfo";
import LoadingScreen from "src/Loading/LoadingScreen";
import { fetchDataApi } from "src/services/impactareaServices";
import { getReferences } from "src/services/reference";
import Control from "react-select/dist/declarations/src/components/Control";
import { ThreeDots } from "react-loader-spinner";
import { isAdminGroup } from 'src/utils/adminGroup';

const getCurrentQuarter = (): string => `Q${Math.floor(new Date().getMonth() / 3) + 1}`;

// Debounce hook for performance optimization
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

export default function RcsaCatalog() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [perPage, setPerPage] = useState<number>(10);
  const [page, setPageNo] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [risks, setRisks] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedRiskId, setSelectedRiskId] = useState<string | null>(null);
  const [data, setData] = useState<any[]>([]);

  const [pageSize, setPageSize] = useState(10);
  const [notAllowed, setNotAllowed] = useState(false);
  // State for quarter and year — defaults to the actual current period rather
  // than a frozen value. null still means "every period" (the "All" option
  // below, or Reset+Apply), just not the initial view any more.
  const [selectedQuarter, setSelectedQuarter] = useState<string | null>(getCurrentQuarter());
  const [selectedYear, setSelectedYear] = useState<number | null>(new Date().getFullYear());
  const [userPermissions, setUserPermissions] = useState<any>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [canUpdate, setCanUpdate] = useState(false);

  // State for impacted areas
  const [impactedAreas, setImpactedAreas] = useState<any[]>([]);
  const [selectedImpactedArea, setSelectedImpactedArea] = useState<string | null>(null);

  // State for edit modal
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [selectedResponse, setSelectedResponse] = useState<string>("");
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  // Click-to-view modal for long text (Examples, Compliance Brief)
  const [isViewModalOpen, setIsViewModalOpen] = useState<boolean>(false);
  const [viewModalTitle, setViewModalTitle] = useState<string>("");
  const [viewModalContent, setViewModalContent] = useState<string>("");

  // Performance optimizations
  const [searchLoading, setSearchLoading] = useState(false);
  
  // Track latest fetch to avoid race conditions and allow cancellation
  const latestFetchIdRef = useRef<number>(0);
  const fetchControllerRef = useRef<AbortController | null>(null);
  
  // Debounced search term for performance
  const debouncedSearchTerm = useDebounce(searchTerm, 500); // 500ms delay
 
  // Client-side filter: Standard (based on Function column)
  const [standardFilter, setStandardFilter] = useState<string | null>(null);

  // Display results count after explicit search
  const [lastSearchTerm, setLastSearchTerm] = useState<string | null>(null);
  const [lastSearchCount, setLastSearchCount] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);

  // Normalize frontend search query to match backend normalization
  const normalizeQuery = (q: string): string => {
    return (
      q
        ?.normalize('NFKC')
        .replace(/\u00A0|\u202F|\u2007/g, ' ') // NBSP-like
        .replace(/[\u200B-\u200D\uFEFF]/g, '') // zero-width chars
        .replace(/\u2013|\u2014/g, '-') // dashes
        .replace(/\u2018|\u2019/g, "'") // single quotes
        .replace(/\u201C|\u201D/g, '"') // double quotes
        .replace(/\s+/g, ' ')
        .trim() || ''
    );
  };

  // Unique standards (functions) from current page data
  const standardOptions = useMemo(() => {
    const setVals = new Set<string>();
    data.forEach((row: any) => {
      const funcs = String(row.function || '')
        .split(',')
        .map((s: string) => s.trim())
        .filter(Boolean);
      funcs.forEach((f: string) => setVals.add(f));
    });
    return Array.from(setVals).sort();
  }, [data]);

  // Apply client-side Standard filter for rendering and export if needed
  const filteredData = useMemo(() => {
    if (!standardFilter) return data;
    return data.filter((row: any) => String(row.function || '').split(',').map((s: string) => s.trim()).includes(standardFilter));
  }, [data, standardFilter]);

  // Update last search count after data/filter change when a search term was submitted
  useEffect(() => {
    if (lastSearchTerm !== null) {
      setLastSearchCount(filteredData.length);
    }
  }, [filteredData, lastSearchTerm]);

  const isRTL = i18n.language === "ar";

  // Rows whose long-text cells are shown in full (the "More" toggle in any
  // cell of the row un-clamps every cell of that row at once).
  const [expandedRows, setExpandedRows] = useState<Set<number>>(() => new Set());
  const toggleRowExpanded = (rowKey: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
  };

  // Control References (Standards) from API and selected standardId
  const [controlReferences, setControlReferences] = useState<any[]>([]);
  const [selectedStandardId, setSelectedStandardId] = useState<string | null>(null);

  // Filters popup (Quarter/Year/Standard) — matches the Services Catalog filter panel
  const [filterPopupOpen, setFilterPopupOpen] = useState(false);
  const filterTriggerRef = useRef<HTMLButtonElement | null>(null);
  const [filterAnchor, setFilterAnchor] = useState<{ top: number; left: number; right: number } | null>(null);
  const [draftQuarter, setDraftQuarter] = useState(selectedQuarter);
  const [draftYear, setDraftYear] = useState(selectedYear);
  const [draftStandardId, setDraftStandardId] = useState<string | null>(selectedStandardId);

  const hasActiveRcsaFilters = selectedStandardId !== null || selectedQuarter !== null || selectedYear !== null;

  const updateFilterAnchor = useCallback(() => {
    if (filterTriggerRef.current) {
      const r = filterTriggerRef.current.getBoundingClientRect();
      setFilterAnchor({ top: r.bottom, left: r.left, right: r.right });
    }
  }, []);

  useLayoutEffect(() => {
    if (filterPopupOpen) updateFilterAnchor();
    else setFilterAnchor(null);
  }, [filterPopupOpen, updateFilterAnchor]);

  useEffect(() => {
    if (!filterPopupOpen) return;
    const fn = () => updateFilterAnchor();
    window.addEventListener('scroll', fn, true);
    window.addEventListener('resize', fn);
    return () => {
      window.removeEventListener('scroll', fn, true);
      window.removeEventListener('resize', fn);
    };
  }, [filterPopupOpen, updateFilterAnchor]);

  const openFilterPanel = () => {
    setDraftQuarter(selectedQuarter);
    setDraftYear(selectedYear);
    setDraftStandardId(selectedStandardId);
    setFilterPopupOpen(true);
  };

  const applyFilterPanel = () => {
    setSelectedQuarter(draftQuarter);
    setSelectedYear(draftYear);
    setSelectedStandardId(draftStandardId);
    setFilterPopupOpen(false);
    setPageNo(0);
    // Fetch here directly, with the draft values passed explicitly, rather
    // than relying on the [selectedQuarter, selectedYear] / [selectedStandardId]
    // effects below: the standardId one only fires when it becomes non-null,
    // so clearing it back to null (Reset -> Apply) silently left the old
    // filtered rows on screen whenever quarter/year didn't also change.
    const normalized = normalizeQuery(searchTerm);
    fetchRCSAData(0, pageSize, normalized, draftStandardId || undefined, draftQuarter, draftYear);
  };

  const resetFilterDraft = () => {
    setDraftQuarter(getCurrentQuarter());
    setDraftYear(new Date().getFullYear());
    setDraftStandardId(null);
  };

  const fetchControlReferences = async () => {
    try {
      const res: any = await getReferences('', 1000, 0);
      // API returns { success, data } where data could be an array
      const items = res?.data || res; // fallback if service returns array directly
      if (Array.isArray(items)) {
        setControlReferences(items);
      }
    } catch (e) {
      console.error('Error fetching control references:', e);
    }
  };

  // Options for quarter and year — the leading null-valued entry clears the
  // filter back to "every period", same as never having picked one.
  const quarterOptions: { value: string | null; label: string }[] = [
    { value: null, label: t('RCSA.allQuarters', { defaultValue: 'All Quarters' }) },
    { value: 'Q1', label: 'Q1' },
    { value: 'Q2', label: 'Q2' },
    { value: 'Q3', label: 'Q3' },
    { value: 'Q4', label: 'Q4' },
  ];

  const yearOptions: any = [
    { value: null, label: t('RCSA.allYears', { defaultValue: 'All Years' }) },
    ...Array.from({ length: 10 }, (_, i) => {
      const year = new Date().getFullYear() - i;
      return { value: year, label: year.toString() };
    }),
  ];

  // Bank response choices for edit modal - will be populated dynamically from API
  const [bankResponseChoices, setBankResponseChoices] = useState<string[]>([]);

  // Helper function to truncate long text with tooltip
  const renderTruncatedText = (text: string, maxLength: number = 100) => {
    if (!text || text.length <= maxLength) {
      return <span>{text}</span>;
    }

    const tooltipId = `tooltip-${Math.random().toString(36).substr(2, 9)}`;

    return (
      <div className="truncated-text-container">
        <span className="truncated-text">{text.substring(0, 80)}...</span>
        <span 
          id={tooltipId}
          className="tooltip-trigger"
          title={text}
        >
          <ThreeDots width={20} height={20} />
        </span>
        <Tooltip
          target={tooltipId}
          placement="top"
          className="text-left"
          style={{
            backgroundColor: '#333',
            color: 'white',
            maxWidth: '400px',
            fontSize: '12px',
            padding: '8px 12px',
            borderRadius: '6px',
            zIndex: 9999
          }}
        >
          <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {text}
          </div>
        </Tooltip>
      </div>
    );
  };

  const isEmptyValue = (value: any) => {
    const v = String(value ?? '').trim();
    return !v || v.toUpperCase() === 'N/A';
  };

  const renderEmptyValue = () => (
    <span className="rcsa-cell-empty" title="N/A" aria-label="N/A">—</span>
  );

  const moreLabel = String(t('RCSA.showMore', { defaultValue: 'More' }));
  const lessLabel = String(t('RCSA.showLess', { defaultValue: 'Less' }));

  // Long-text cell: CSS line clamp (2–3 lines) instead of cutting words off.
  // The full value is always in the native title tooltip, and a "More"/"Less"
  // toggle un-clamps the whole row. `charsPerLine` is a rough per-column
  // capacity used only to decide whether the toggle is worth showing.
  const renderClampCell = (value: string, rowKey: number, lines: 2 | 3 = 3, charsPerLine: number = 36) => {
    if (isEmptyValue(value)) return renderEmptyValue();
    const text = String(value).trim();
    const expanded = expandedRows.has(rowKey);
    const lineCount = text.split('\n').length;
    const isLong = text.length > lines * charsPerLine || lineCount > lines;

    return (
      <div className="rcsa-cell">
        <div
          className={`rcsa-clamp rcsa-clamp--${lines}${expanded ? ' is-expanded' : ''}`}
          title={text}
        >
          {text}
        </div>
        {isLong && (
          <button
            type="button"
            className="rcsa-more-btn"
            onClick={() => toggleRowExpanded(rowKey)}
            aria-expanded={expanded}
          >
            {expanded ? lessLabel : moreLabel}
          </button>
        )}
      </div>
    );
  };

  // Bank response → soft status badge tone.
  const getResponseTone = (value: string): 'na' | 'partial' | 'negative' | 'positive' | 'neutral' => {
    const v = String(value || '').trim().toLowerCase();
    if (!v || v === 'n/a' || v === 'na' || v.includes('not applicable') || v.includes('غير قابل') || v.includes('لا ينطبق')) return 'na';
    if (v.includes('partial') || v.includes('جزئي')) return 'partial';
    if (
      v === 'no' ||
      v.startsWith('not ') ||
      v.startsWith('non') ||
      v.startsWith('unimplemented') ||
      v.includes('not implemented') ||
      v.includes('non-compliant') ||
      v.startsWith('لا') ||
      v.startsWith('غير')
    ) return 'negative';
    if (v === 'yes' || v.includes('implemented') || v.includes('compliant') || v.includes('fully') || v === 'نعم' || v.includes('مطبق') || v.includes('ممتثل')) return 'positive';
    return 'neutral';
  };

  // Helper function to copy control text
  const copyControlText = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t("Copied") || "Copied");
    } catch (e) {
      console.error("Clipboard copy failed", e);
      toast.error(t("Copy failed") || "Copy failed");
    }
  };

  // Control Mapping cell: each mapped control is a readable (non-uppercase,
  // non-underlined) link clamped to 2 lines with the full text in its title.
  // Only the first two controls show until the row is expanded ("More").
  const renderClickableControls = (item: any) => {
    const rowKey: number = item?.index;
    const expanded = expandedRows.has(rowKey);
    const VISIBLE_CONTROLS = 2;

    // Prefer parsing from the raw item controls JSON when available
    let parsedControls: any[] = [];
    const rawControls = item?.rawItem?.controls;

    if (rawControls) {
      try {
        const controlsJson = safeParseJSON(rawControls);
        if (Array.isArray(controlsJson)) {
          parsedControls = controlsJson;
        }
      } catch (e) {
        // Fall back below
      }
    }

    type ControlEntry = { label: string; isClickable: boolean; onClick?: () => void };
    let entries: ControlEntry[] = [];

    if (parsedControls.length) {
      const complianceId = item?.rawItem?.surveyAssessmentId || item?.rawItem?.id || item?.rawItem?.surveyAssessment?.id;
      entries = parsedControls.map((control: any) => {
        const controlId = control?.id;
        const domainId = control?.domains?.[0]?.id;
        const isClickable = Boolean(controlId && complianceId && domainId);
        return {
          label: control?.description || 'N/A',
          isClickable,
          onClick: isClickable
            ? () => navigate(`/compliances/view-control/${controlId}/${complianceId}/${domainId}`)
            : undefined,
        };
      });
    } else if (!isEmptyValue(item?.controlMapping)) {
      // Fallback: non-clickable labels from the aggregated mapping string
      entries = String(item.controlMapping)
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean)
        .map((label) => ({ label, isClickable: false }));
    }

    entries = entries.filter((e) => !isEmptyValue(e.label));
    if (!entries.length) return renderEmptyValue();

    const shown = expanded ? entries : entries.slice(0, VISIBLE_CONTROLS);
    const hiddenCount = entries.length - VISIBLE_CONTROLS;
    const needsToggle = hiddenCount > 0 || entries.some((e) => e.label.length > 2 * 38);
    const copyTitle = String(t("Copy this control") || "Copy this control");

    return (
      <div className="rcsa-cell">
        <ul className="rcsa-control-list">
          {shown.map((entry, idx) => (
            <li key={idx} className="rcsa-control-item">
              {entry.isClickable ? (
                <button
                  type="button"
                  className="rcsa-control-link"
                  onClick={entry.onClick}
                  title={entry.label}
                >
                  {/* Clamp on an inner span: line-clamp on a <button> box itself is unreliable. */}
                  <span className={`rcsa-clamp rcsa-clamp--2${expanded ? ' is-expanded' : ''}`}>
                    {entry.label}
                  </span>
                </button>
              ) : (
                <span
                  className={`rcsa-control-text rcsa-clamp rcsa-clamp--2${expanded ? ' is-expanded' : ''}`}
                  title={entry.label}
                >
                  {entry.label}
                </span>
              )}
              <button
                type="button"
                className="rcsa-copy-btn"
                onClick={() => copyControlText(entry.label)}
                title={copyTitle}
                aria-label={copyTitle}
              >
                <MdContentCopy size={13} aria-hidden />
              </button>
            </li>
          ))}
        </ul>
        {needsToggle && (
          <button
            type="button"
            className="rcsa-more-btn"
            onClick={() => toggleRowExpanded(rowKey)}
            aria-expanded={expanded}
          >
            {expanded ? lessLabel : hiddenCount > 0 ? `${moreLabel} (+${hiddenCount})` : moreLabel}
          </button>
        )}
      </div>
    );
  };

  // Function to fetch impacted areas
  const fetchImpactedAreas = async () => {
    try {
      const data: any = await fetchDataApi(`impacted-areas?perPage=1000&pageNo=0&query=`);
      if (data.success) {
        setImpactedAreas(data.data);
      }
    } catch (error: any) {
      console.error('Error fetching impacted areas:', error);
    }
  };

  // Convert impacted areas to select options
  const impactedAreaOptions = impactedAreas.map((area: any) => ({
    value: area.id,
    label: area.name
  }));

  const exportToExcel = async () => {
      setLoading(true);
    
      try {
        // Use the last submitted search term if available; fallback to current input
        const exportQuery = normalizeQuery((lastSearchTerm !== null ? lastSearchTerm : searchTerm).trim());
        // Fetch up to 500 rows from the same API for export
        const exportResp = await getRCSAByPage(
          selectedQuarter,
          selectedYear,
          0,
          500,
          exportQuery,
          selectedStandardId || undefined
        );

        let dataToExport: any[] = [];
        if (exportResp?.success) {
          const rawData = exportResp?.data?.data || [];
          // Process rows similarly to fetchRCSAData mapping
          dataToExport = rawData
            .map((item: any, index: number) => {
              if (!item?.question_en) return null;

              let questionText = item.question_en || "N/A";
              let controlMappingSet = new Set<string>();
              let evidenceSet = new Set<string>();
              let complianceBriefSet = new Set<string>();
              let impactedAreaSet = new Set<string>();
              let functionSetItem = new Set<string>();
              let domainSetItem = new Set<string>();
              let ownerSet = new Set<string>();
              let controlType = "N/A";

              if (item?.controls) {
                try {
                  const controls = safeParseJSON(item.controls);
                  if (Array.isArray(controls)) {
                    controls.forEach((control: any) => {
                      if (control?.domains?.length > 0) {
                        control.domains.forEach((d: any) => {
                          const funcName = d.parentName || "N/A";
                          const domainName = d.name || "N/A";
                          if (funcName !== "N/A") functionSetItem.add(funcName);
                          if (domainName !== "N/A") domainSetItem.add(domainName);
                        });
                      }
                      if (control?.description && control.description !== "N/A") {
                        controlMappingSet.add(control.description);
                      }
                      // Owner = the control's Supervisor (its Supervisor box)
                      if (control?.supervisorName) ownerSet.add(control.supervisorName);
                      controlType =
                        control?.keyControl === true
                          ? "Mandatory"
                          : control?.keyControl === false
                          ? "Advisory"
                          : "N/A";
                      if (control?.complianceActions?.length > 0) {
                        control.complianceActions.forEach((action: any) => {
                          if (action?.evidence && action.evidence !== "N/A") {
                            evidenceSet.add(action.evidence);
                          }
                          if (action?.justification && action.justification !== "N/A") {
                            complianceBriefSet.add(action.justification);
                          }
                          if (action?.impactedAreas?.length > 0) {
                            action.impactedAreas.forEach((ia: any) => {
                              if (ia?.name) impactedAreaSet.add(ia.name);
                            });
                          }
                        });
                      }
                    });
                  }
                } catch {}
              }

              return {
                index: index + 1,
                function: Array.from(functionSetItem).join(", ") || "N/A",
                domain: Array.from(domainSetItem).join(", ") || "N/A",
                question: questionText,
                bankResponse: item?.answer || "N/A",
                controlType,
                examples: Array.from(evidenceSet).join("\n") || "N/A",
                controlMapping: Array.from(controlMappingSet).join("\n") || "N/A",
                complianceBrief: Array.from(complianceBriefSet).join("\n") || "N/A",
                impactedArea: Array.from(impactedAreaSet).join(", ") || "N/A",
                owner: Array.from(ownerSet).join(", ") || "N/A",
              };
            })
            .filter(Boolean) as any[];

          // Apply Standard (Function) filter to export if selected
          if (standardFilter) {
            dataToExport = dataToExport.filter((row: any) => String(row.function || '').split(',').map((s: string) => s.trim()).includes(standardFilter));
          }
        } else {
          // Fallback to currently displayed data if API fails
          dataToExport = (standardFilter
            ? data.filter((row: any) => String(row.function || '').split(',').map((s: string) => s.trim()).includes(standardFilter))
            : data);
        }
        
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Bank's Self Assessment");
    
        // Use processed data from API
    
        // Add headers
        worksheet.columns = [
          { header: '#', key: 'index', width: 8 },
          { header: t("Function") as string, key: "function", width: 30 },
          { header: t("Domain") as string, key: "domain", width: 30 },
          { header: t("Question") as string, key: "question", width: 60 },
          { header: t("Bank Response") as string, key: "bankResponse", width: 20 },
          { header: t("Control Type") as string, key: "controlType", width: 15 },
          { header: t("Examples of Required Samples") as string, key: "examples", width: 40 },
          { header: t("Control Mapping") as string, key: "controlMapping", width: 50 },
          { header: t("Bank Compliance Brief / Justification") as string, key: "complianceBrief", width: 50 },
          { header: t("Impacted/Affected Area") as string, key: "impactedArea", width: 30 },
          { header: t("Owner") as string, key: "owner", width: 25 },
        ];
    
        // Style header
        const headerRow = worksheet.getRow(1);
        headerRow.height = 30;
        headerRow.eachCell((cell) => {
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "000000" } };
          cell.font = { color: { argb: "FFFFFF" }, bold: true };
          cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
          cell.border = { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } };
        });
    
        // Add data rows
        dataToExport.forEach((row: any) => {
          const newRow = worksheet.addRow(row);
 
          // Index Column
          const indexCell = newRow.getCell('index');
          indexCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE5E7EB' } }; // light gray
          indexCell.font = { color: { argb: 'FF000000' } };
          indexCell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
          indexCell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };

          // Function Column
          const functionCell = newRow.getCell("function");
          functionCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD0B162" } };
          functionCell.font = { color: { argb: "000000" } };
          functionCell.alignment = { vertical: "middle", horizontal: "left", wrapText: true };
          functionCell.border = { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } };
    
          // Domain Column
          const domainCell = newRow.getCell("domain");
          domainCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF2E9D2" } };
          domainCell.font = { color: { argb: "000000" } };
          domainCell.alignment = { vertical: "middle", horizontal: "left", wrapText: true };
          domainCell.border = { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } };
    
          // Control Type Column
          const controlTypeCell = newRow.getCell("controlType");
          const mandatoryColor = "FFDC4A4A";
          const advisoryColor = "FFE8C412";
          const defaultColor = "FFFFFFFF";
          controlTypeCell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: row.controlType === "Mandatory" ? mandatoryColor : row.controlType === "Advisory" ? advisoryColor : defaultColor },
          };
          controlTypeCell.font = {
            color: { argb: row.controlType === "Advisory" ? "FF000000" : "FFFFFFFF" },
          };
          controlTypeCell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
          controlTypeCell.border = { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } };
    
          // Wrap text for long columns
          ["question", "examples", "controlMapping", "complianceBrief", "impactedArea"].forEach((key) => {
            const cell = newRow.getCell(key);
            cell.alignment = { wrapText: true, vertical: "middle", horizontal: "left" };
          });
        });
    
        // Save workbook
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: "application/octet-stream" });
        saveAs(blob, "Bank's Self Assessment.xlsx");
      } catch (err) {
        console.error("Error exporting Excel:", err);
        toast.error("Failed to export Excel file");
      } finally {
        setLoading(false);
      }
  };
    


  // Custom styles for react-select — sized and colored to match the app's
  // standard toolbar controls (Standards/Services/Surveys).
  const customSelectStyles = {
    // Every filter control is a 38px box with the value vertically centered
    // (the old fixed 30px inner heights inside a 32px+ control pushed the
    // value toward the bottom edge).
    control: (provided: any, state: any) => ({
      ...provided,
      minHeight: '38px',
      borderColor: state.isFocused ? '#1a5c3d' : '#d1d5db',
      borderWidth: '1px',
      borderRadius: 8,
      backgroundColor: '#fff',
      boxShadow: state.isFocused ? '0 0 0 3px rgba(26,92,61,0.15)' : 'none',
      cursor: 'pointer',
      '&:hover': { borderColor: '#1a5c3d' },
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      minHeight: '36px',
      padding: '0 12px',
      alignItems: 'center',
      fontSize: '0.875rem',
    }),
    singleValue: (provided: any) => ({
      ...provided,
      margin: 0,
      color: '#1f2937',
      lineHeight: 1.3,
    }),
    placeholder: (provided: any) => ({
      ...provided,
      margin: 0,
      color: '#9ca3af',
      lineHeight: 1.3,
    }),
    input: (provided: any) => ({
      ...provided,
      margin: '0px',
      padding: '0px',
    }),
    indicatorsContainer: (provided: any) => ({
      ...provided,
      alignSelf: 'stretch',
      alignItems: 'center',
    }),
    dropdownIndicator: (provided: any, state: any) => ({
      ...provided,
      color: state.isFocused ? '#1a5c3d' : '#6b7280',
      padding: '0 10px',
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      fontSize: '0.875rem',
      cursor: 'pointer',
      backgroundColor: state.isSelected ? '#1a5c3d' : state.isFocused ? 'rgba(26,92,61,0.08)' : '#fff',
      color: state.isSelected ? '#fff' : '#1f2937',
      '&:active': { backgroundColor: 'rgba(26,92,61,0.16)' },
    }),
    menu: (provided: any) => ({
      ...provided,
      borderRadius: 8,
      overflow: 'hidden',
      boxShadow: '0 10px 24px -6px rgba(0,0,0,0.15)',
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    // These Selects render their menu through menuPortalTarget={document.body},
    // as a sibling of the filter popup rather than nested inside it — react-select's
    // own default portal z-index (1) sits well below the popup's 1060, so without
    // this override every option list rendered behind the panel and was unclickable.
    menuPortal: (provided: any) => ({ ...provided, zIndex: 9999 }),
  };

  const handlePagination = (newPage: number) => {
    if (newPage >= 0 && newPage < totalPages) {
      setPageNo(newPage);
      // Fetch data for the new page from backend — keep the active search
      // (normalized, as every other fetch does) and Standard filter, otherwise
      // page 2+ silently dropped the Standard filter.
      fetchRCSAData(newPage, pageSize, normalizeQuery(searchTerm), selectedStandardId || undefined);
    }
  };

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  // Edit modal functions
  const openEditModal = (item: any) => {
    if (!item) {
      toast.error("Invalid item selected");
      return;
    }
    
    // Extract choices from the raw item's choices property
    if (item.rawItem?.choices) {
      const choicesArray = item.rawItem.choices.split(',').map((choice: string) => choice.trim());
      setBankResponseChoices(choicesArray);
    } else {
      // Fallback to default choices if none found
      setBankResponseChoices(["Yes", "No", "Partially Implemented", "Not Applicable"]);
    }
    
    setEditingItem(item);
    setSelectedResponse(item.bankResponse || "");
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditingItem(null);
    setSelectedResponse("");
  };

  const handleResponseChange = (response: string) => {
    setSelectedResponse(response);
  };

  const openViewModal = (title: string, content: string) => {
    setViewModalTitle(title);
    setViewModalContent(content || "");
    setIsViewModalOpen(true);
  };

  const closeViewModal = () => {
    setIsViewModalOpen(false);
    setViewModalTitle("");
    setViewModalContent("");
  };

  const copyViewContent = async () => {
    try {
      await navigator.clipboard.writeText(viewModalContent);
      toast.success(t("Copied") || "Copied");
    } catch (e) {
      console.error("Clipboard copy failed", e);
      toast.error(t("Copy failed") || "Copy failed");
    }
  };

 const handleUpdateResponse = async () => {
  if (!editingItem || !selectedResponse) {
    toast.error("Please select a response");
    return;
  }

  // Check permissions before proceeding
  if (!canUpdate) {
    toast.error("You don't have permission to update bank responses");
    return;
  }

  // Store current page before update
  const currentPageBeforeUpdate = page;

  setIsUpdating(true);
  try {
    // Find the raw item to get the survey assessment ID
    const rawItem = editingItem.rawItem;
    
    if (!rawItem) {
      toast.error("Survey assessment data not found");
      return;
    }

    const surveyAssessmentId = rawItem.id || rawItem.surveyAssessmentId || rawItem.surveyAssessment?.id;
    
    if (!surveyAssessmentId) {
      toast.error("Survey assessment ID not found");
      return;
    }

    const updateData = {
      answer: selectedResponse
    };

    // Optimistic update
    const previousData = data;
    const optimisticallyUpdated = data.map(item =>
      item.index === editingItem.index 
        ? { ...item, bankResponse: selectedResponse }
        : item
    );
    setData(optimisticallyUpdated);
    // Close modal immediately for responsive UX
    closeEditModal();

    const response = await updateSurveyAssessmentApi(surveyAssessmentId, updateData);
    
    if (response.success) {
      toast.success("Bank response updated successfully");
    } else {
      // Rollback on failure
      setData(previousData);
      toast.error(response.message || "Failed to update bank response");
    }
  } catch (error: any) {
    console.error("Error updating bank response:", error);
    // Rollback on error
    setData(prev => prev.map(item =>
      item.index === editingItem?.index ? { ...item, bankResponse: editingItem?.bankResponse } : item
    ));
    toast.error(error.message || "An error occurred while updating");
  } finally {
    setIsUpdating(false);
  }
};

  const fetchUserRolePermissions = async () => {
    try {
      const permissions = await getUserRolePermissions();
     
      if (permissions.success) {
        const userPagePermission = permissions.data.permissions.find((permission: any) => permission.page === "RCSA");
        
        if (userPagePermission) {
          setUserPermissions(userPagePermission);
          setNotAllowed(!userPagePermission.show);
          // Check update permission
          setCanUpdate(userPagePermission.edit || false);
          
          // Check if user is Super Admin
          const userRole = getUserRole();
          setIsSuperAdmin(isAdminGroup(userRole));
          
          // Fetch data initially
          await fetchRCSAData(0, pageSize, "", undefined);
        } else {
          setNotAllowed(true);
        }
      } else {
        setNotAllowed(true);
      }
    } catch (error: any) {
      toast.error(t("permissions.Error_fetching_user_role_permissions") + ":", error);
    }
  };


  function safeParseJSON(jsonString: string) {
    try {
      return JSON.parse(jsonString);
    } catch (e:any) {
      console.error("First parse failed:", e.message);
  
      // Attempt cleaning
      let fixed = jsonString
        .replace(/[\r\n\t]/g, " ") // remove line breaks and tabs
        .replace(/'/g, '"') // convert single quotes to double
        .replace(/,\s*}/g, "}") // remove trailing commas in objects
        .replace(/,\s*]/g, "]"); // remove trailing commas in arrays
  
      try {
        return JSON.parse(fixed);
      } catch (e2:any) {
        console.error("Second parse failed:", e2.message);
        console.error("Problematic string:", fixed);
        return null; // fallback
      }
    }
  }
  




  const fetchRCSAData = async (
    pageNum: number = 0,
    pageSizeNum: number = pageSize,
    searchQuery: string = "",
    controlReferenceId?: string,
    // Explicit overrides for Apply: setSelectedQuarter/setSelectedYear haven't
    // flushed to a re-render yet when applyFilterPanel calls this in the same
    // handler, so reading them from closure here would still see the OLD
    // value — pass the just-picked draft values straight through instead.
    quarterOverride: string | null = selectedQuarter,
    yearOverride: number | null = selectedYear,
  ) => {
    // Cancel any in-flight request
    try {
      fetchControllerRef.current?.abort();
    } catch {}

    const controller = new AbortController();
    fetchControllerRef.current = controller;

    const currentFetchId = ++latestFetchIdRef.current;

    setLoading(true);
    setSearchLoading(!!searchQuery);
  
    try {
      const response = await getRCSAByPage(
        quarterOverride,
        yearOverride,
        pageNum,
        pageSizeNum,
        searchQuery,
        controlReferenceId
      );
      
      // If a newer request has been fired or this one was aborted, ignore
      if (currentFetchId !== latestFetchIdRef.current || controller.signal.aborted) {
        return;
      }

      if (response.success) {
        const rawData = response?.data?.data || [];
        const totalPages = response?.data?.totalPages || 0;
        const totalCountResp = response?.data?.total || 0;
  
        const functionSet = new Set<string>();
  
        const processedData = rawData
          .map((item: any, index: number) => {
            if (!item?.question_en) return null;
  
            let questionText = item.question_en || "N/A";
            let controlMappingSet = new Set<string>();
            let evidenceSet = new Set<string>();
            let complianceBriefSet = new Set<string>();
            let impactedAreaSet = new Set<string>();
            let functionSetItem = new Set<string>();
            let domainSetItem = new Set<string>();
            let ownerSet = new Set<string>();
            let controlType = "N/A";
  
            // Parse controls
            if (item?.controls) {
              try {
                const controls = safeParseJSON(item.controls);
  
                if (Array.isArray(controls)) {
                  controls.forEach((control: any) => {
                    // Domains and function
                    if (control?.domains?.length > 0) {
                      control.domains.forEach((d: any) => {
                        const funcName = d.parentName || "N/A";
                        const domainName = d.name || "N/A";
                        if (funcName !== "N/A") {
                          functionSetItem.add(funcName);
                          functionSet.add(funcName); // global set
                        }
                        if (domainName !== "N/A") {
                          domainSetItem.add(domainName);
                        }
                      });
                    }
  
                    // Control description
                    if (control?.description && control.description !== "N/A") {
                      controlMappingSet.add(control.description);
                    }

                    // Owner = the control's Supervisor (its Supervisor box)
                    if (control?.supervisorName) ownerSet.add(control.supervisorName);
  
                    // Control type
                    controlType =
                      control?.keyControl === true
                        ? "Mandatory"
                        : control?.keyControl === false
                        ? "Advisory"
                        : "N/A";
  
                    // Compliance Actions
                    if (control?.complianceActions?.length > 0) {
                      control.complianceActions.forEach((action: any) => {
                        if (action?.evidence && action.evidence !== "N/A") {
                          evidenceSet.add(action.evidence);
                        }
                        if (action?.justification && action.justification !== "N/A") {
                          complianceBriefSet.add(action.justification);
                        }
                        if (action?.impactedAreas?.length > 0) {
                          action.impactedAreas.forEach((ia: any) => {
                            if (ia?.name) impactedAreaSet.add(ia.name);
                          });
                        }
                      });
                    }
                  });
                }
              } catch (err) {
                console.error("Error parsing controls:", err);
              }
            }
  
  
            return {
              index: pageNum * pageSizeNum + index + 1,
              function: Array.from(functionSetItem).join(", ") || "N/A",
              domain: Array.from(domainSetItem).join(", ") || "N/A",
              question: questionText,
              bankResponse: item?.answer || "N/A",
              controlType,
              examples: Array.from(evidenceSet).join("\n") || "N/A",
              controlMapping: Array.from(controlMappingSet).join("\n") || "N/A",
              complianceBrief: Array.from(complianceBriefSet).join("\n") || "N/A",
              impactedArea: Array.from(impactedAreaSet).join(", ") || "N/A",
              owner: Array.from(ownerSet).join(", ") || "N/A",
              rawItem: item,
            };
          })
          .filter(Boolean);
  
        setData(processedData);
        setTotalPages(totalPages);
        setTotalCount(totalCountResp);
  
       // console.log("Unique Functions:", Array.from(functionSet));
      } else {
        toast.error(response.message);
        setData([]);
        setTotalPages(0);
      }
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        // Silently ignore aborted requests
        return;
      }
      console.error("Error fetching RCSA data:", error);
      toast.error("An error occurred while fetching data");
      setData([]);
      setTotalPages(0);
    } finally {
      if (currentFetchId === latestFetchIdRef.current) {
        setLoading(false);
        setSearchLoading(false);
      }
    }
  };
  


  useEffect(() => {
    const fetchUserData = async () => {
      try {
        await fetchUserRolePermissions();
        await fetchImpactedAreas();
        await fetchControlReferences();
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
  
    fetchUserData();
  }, []);

  // Refetch from backend when quarter or year changes
  useEffect(() => {
    fetchRCSAData(0, pageSize, searchTerm, selectedStandardId || undefined);
  }, [selectedQuarter, selectedYear]);

  // Refetch from backend when control reference changes
  useEffect(() => {
    if (selectedStandardId !== null) {
      setPageNo(0);
      const normalized = normalizeQuery(searchTerm);
      fetchRCSAData(0, pageSize, normalized, selectedStandardId);
    }
  }, [selectedStandardId]);

  const handleSearchSubmit = async () => {
    setPageNo(0);
    const normalized = normalizeQuery(searchTerm);
    await fetchRCSAData(0, pageSize, normalized, selectedStandardId || undefined);
    setLastSearchTerm(normalized);
  };

  // Live search — fires automatically as the (debounced) search term settles,
  // matching the single-search-input pattern used on the other list pages.
  useEffect(() => {
    setPageNo(0);
    const normalized = normalizeQuery(debouncedSearchTerm);
    fetchRCSAData(0, pageSize, normalized, selectedStandardId || undefined);
    setLastSearchTerm(normalized || null);
  }, [debouncedSearchTerm]);

  const handleReset = async () => {
    setSearchTerm("");
    setStandardFilter(null);
    setSelectedStandardId(null);
    setLastSearchTerm(null);
    setLastSearchCount(0);
    setPageNo(0);
    await fetchRCSAData(0, pageSize, "", undefined);
  };

  const handleSmartSearch = (searchValue: string) => {
    setSearchTerm(searchValue);
  };

  const clearSearch = () => {
    setSearchTerm("");
    setPageNo(0);
    fetchRCSAData(0, pageSize, "");
  };

  const handleDownloadTemplate = () => {
    const rcsaData = [
      [
        "Survey Name", 
        "Category", 
        "Reference", 
        "Question", 
        "Control Mapping", 
        "Bank Response", 
        "Control Type",
        "Quarter", 
        "Year", 
        "Due Date", 
        "Domain", 
        "Bank Compliance Brief Description/Justifications", 
        "Supervisor",
        "Manager",
        "Impacted/Affected Area", 
        "answerdby"
      ],
      [
        "Survey 1", 
        "Category 1", 
        "Reference 1", 
        "Question 1", 
        "Control 1", 
        "Implemented", 
        "Mandatory",
        "Q1", 
        "2024", 
        "2024-03-31", 
        "Domain 1", 
        "Description 1", 
        "Supervisor Name",
        "Manager Name",
        "Area 1, Area 2", 
        "User Name"
      ],
    ];
  
    const workbook = XLSX.utils.book_new();
    const rcsaSheet = XLSX.utils.aoa_to_sheet(rcsaData);
    XLSX.utils.book_append_sheet(workbook, rcsaSheet, 'RCSA');
    XLSX.writeFile(workbook, "Bank's Self Assessment.xlsx");
  };
  
  const handleImportExcel = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
        setLoading(true);
        try {
            const response = await uploadExcelForRCSA(file);
            toast.success(response.message || "Excel file imported successfully");
            // Refresh data after import
            await fetchRCSAData(0, pageSize, searchTerm);
        } catch (error) {
            toast.error("Error importing Excel file");
        }
        setLoading(false);
    }
  };



  

  if (loading) {
    return (
      <div className="loading-screen" 
      style={{ 
        backgroundColor: 'rgba(255, 255, 255, 0.8)'}}>
        <div><LoadingScreen/></div>
      </div>
    );
  }

  const periodLabel: string =
    selectedQuarter || selectedYear
      ? `${selectedQuarter || ''} ${selectedYear || ''}`.trim()
      : String(t('common.allPeriods', { defaultValue: 'All periods' }));
  const standardLabel: string = selectedStandardId
    ? (controlReferences || []).find((cr: any) => cr.id === selectedStandardId)?.name || ''
    : String(t('All Standards') || 'All Standards');

  // Fixed per-column widths (table-layout: fixed) — long-text columns get
  // 280–320px so text wraps at word boundaries instead of mid-word.
  const rcsaColumns: { key: string; label: string; width: number; className?: string }[] = [
    { key: 'index', label: '#', width: 52, className: 'rcsa-col-index' },
    { key: 'function', label: String(t("Function")), width: 150 },
    { key: 'domain', label: String(t("Domain")), width: 170 },
    { key: 'question', label: String(t("Question")), width: 320 },
    { key: 'bankResponse', label: String(t("Bank Response")), width: 180 },
    { key: 'controlType', label: String(t("Control Type")), width: 124 },
    { key: 'examples', label: String(t("Examples of Required Samples")), width: 300 },
    { key: 'controlMapping', label: String(t("Control Mapping")), width: 300 },
    { key: 'complianceBrief', label: String(t("Bank Compliance Brief / Justification")), width: 320 },
    { key: 'impactedArea', label: String(t("Impacted/Affected Area")), width: 170 },
    { key: 'owner', label: String(t("Owner")), width: 150 },
  ];

  return (
    <div className="root">
      <Breadcrumb className="mb-1">
        <BreadcrumbItem>{t("header.you_are_here")}</BreadcrumbItem>
        <BreadcrumbItem active style={{ paddingRight: "10px" }}>
          {t("Risk.RCSA")}
        </BreadcrumbItem>
      </Breadcrumb>

      {!notAllowed && <div
        className="container_info"
        style={{ padding: "0 12px"}}
      >
        {/* Header card: search + search/reset, primary actions on the right */}
        <div className="services-catalog-page-header card border-0 shadow-sm mb-2">
          <div className="card-body py-2 px-3 px-lg-4">
            <div className="d-flex flex-wrap align-items-center gap-2 gap-md-3 justify-content-between">
              <div className="position-relative flex-grow-1 rcsa-search-wrap" style={{ minWidth: 200, maxWidth: 420 }}>
                <HiMagnifyingGlass
                  className="position-absolute text-muted"
                  size={16}
                  style={{
                    ...(isRTL ? { right: 12, left: 'auto' } : { left: 12, right: 'auto' }),
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                  }}
                  aria-hidden
                />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => handleSmartSearch(e.target.value.replace(/^\s+/, ''))}
                  placeholder={t("Search...") || 'Search...'}
                  aria-label={String(t("Search...") || 'Search...')}
                  className="form-control border bg-white services-catalog-search-input rcsa-search-input"
                  dir={isRTL ? 'rtl' : 'ltr'}
                  style={{
                    width: '100%',
                    ...(isRTL ? { paddingRight: 36, paddingLeft: 12 } : { paddingLeft: 36, paddingRight: 12 }),
                  }}
                />
              </div>

              <div className="d-flex flex-wrap gap-2 align-items-center justify-content-end services-catalog-primary-actions services-catalog-header-actions-gap">
                {(userPermissions?.create || isSuperAdmin) && (
                  <>
                    <button
                      type="button"
                      className="btn btn-download-template btn-sm d-inline-flex align-items-center justify-content-center gap-1"
                      onClick={handleDownloadTemplate}
                    >
                      <MdOutlineDownload size={16} aria-hidden />
                      {t("References_Page.download_excel_template")}
                    </button>
                    <input
                      type="file"
                      accept=".xlsx, .xls"
                      onChange={handleImportExcel}
                      style={{ display: 'none' }}
                      id="import-excel"
                    />
                    <label
                      htmlFor="import-excel"
                      className="btn btn-upload-excel btn-sm d-inline-flex align-items-center justify-content-center gap-1 mb-0"
                      style={{ cursor: 'pointer' }}
                    >
                      <MdOutlineUpload size={16} aria-hidden />
                      {t("References_Page.import_excel")}
                    </label>
                  </>
                )}
                {/* Export reads the same list the page shows (RCSA show), so it needs show, not create. */}
                {userPermissions?.show && (
                  <button
                    type="button"
                    className="btn btn-download-template btn-sm d-inline-flex align-items-center justify-content-center gap-1"
                    onClick={exportToExcel}
                    disabled={loading}
                  >
                    <MdOutlineFileDownload size={16} aria-hidden />
                    {t('Export to Excel') || 'Export to Excel'}
                  </button>
                )}
              </div>
            </div>

            {/* Search Results Counter (shows after Search is submitted) */}
            {lastSearchTerm !== null && (
              <div className="d-flex align-items-center gap-2 mt-2" style={{
                padding: '10px 16px',
                backgroundColor: '#f0fdf4',
                border: '1px solid #a7f3d0',
                borderRadius: '8px',
                fontSize: '13px',
                color: '#166534'
              }}>
                <span>
                  {searchLoading ? (
                    'Searching...'
                  ) : (
                    `${t("Found") || 'Found'} ${lastSearchCount} ${t("of") || 'of'} ${totalCount} ${t("questions matching") || 'questions matching'} "${lastSearchTerm}"`
                  )}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Filters card: Quarter / Year / Standard, grouped under a Filters label */}
        <div className="services-catalog-table-toolbar controls-catalog-tools-card card border-0 shadow-sm mb-2">
          <div className="card-body py-2 px-3 px-lg-4">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <span className="rcsa-showing-label">
                  {t('common.activeFilters', { defaultValue: 'Showing' })}
                </span>
                <span className="rcsa-showing-value">
                  {periodLabel} · {standardLabel}
                </span>
                <span className="rcsa-showing-hint">
                  <span className="rcsa-showing-hint__sep" aria-hidden>—</span>
                  <span>{t('RCSA.filtersHint', { defaultValue: 'To view another quarter, year or standard, use Filters' })}</span>
                  <button
                    type="button"
                    className="rcsa-showing-change"
                    onClick={openFilterPanel}
                    aria-haspopup="dialog"
                    aria-expanded={filterPopupOpen}
                  >
                    {t('RCSA.changeFilters', { defaultValue: 'Change' })}
                  </button>
                </span>
              </div>
              <div className="d-flex align-items-center gap-2">
                <div
                  className={`controls-catalog-tab-pair rcsa-tab-pair ${filterPopupOpen ? 'controls-catalog-tab-pair--open' : ''} ${hasActiveRcsaFilters ? 'controls-catalog-tab-pair--has-filters' : ''}`}
                  role="group"
                  aria-label={String(t('common.filters', { defaultValue: 'Filters' }))}
                >
                  <span className="controls-catalog-tab-pair__first">
                    {String(t('controls.catalogViewTab', { defaultValue: 'List' }))}
                  </span>
                  <button
                    ref={filterTriggerRef}
                    type="button"
                    className="controls-catalog-tab-pair__filter"
                    onClick={() => (filterPopupOpen ? setFilterPopupOpen(false) : openFilterPanel())}
                    aria-label={String(t('controls.filter', { defaultValue: 'Filter' }))}
                    aria-expanded={filterPopupOpen}
                    aria-pressed={filterPopupOpen}
                  >
                    <FilterIcon size={18} aria-hidden />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {typeof document !== 'undefined' && createPortal(
          <>
            {filterPopupOpen && filterAnchor && (
              <div
                className="controls-catalog-popup-panel rcsa-filter-panel"
                dir={isRTL ? 'rtl' : 'ltr'}
                style={{ position: 'fixed', top: filterAnchor.top + 4, right: window.innerWidth - filterAnchor.right, zIndex: 1060 }}
                role="dialog"
                aria-label={String(t('common.filters', { defaultValue: 'Filters' }))}
              >
                <div className="controls-catalog-popup-header">
                  <span className="controls-catalog-popup-title">
                    {t('common.filters', { defaultValue: 'Filters' })}
                  </span>
                  <button
                    type="button"
                    className="controls-catalog-popup-close"
                    aria-label={String(t('common.close', { defaultValue: 'Close' }))}
                    onClick={() => setFilterPopupOpen(false)}
                  >
                    &times;
                  </button>
                </div>
                <div className="controls-catalog-popup-body">
                  <div className="rcsa-filter-fields">
                    <div className="rcsa-filter-field">
                      <label className="rcsa-filter-label" htmlFor="rcsa-filter-quarter">
                        {t('RCSA.quarter', { defaultValue: 'Quarter' })}
                      </label>
                      <Select
                        inputId="rcsa-filter-quarter"
                        classNamePrefix="rcsa-select"
                        options={quarterOptions}
                        styles={customSelectStyles}
                        placeholder={String(t('RCSA.quarter', { defaultValue: 'Quarter' }))}
                        value={quarterOptions.find((option: any) => option.value === draftQuarter)}
                        onChange={(option: any) => setDraftQuarter(option?.value ?? null)}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        isRtl={isRTL}
                      />
                    </div>
                    <div className="rcsa-filter-field">
                      <label className="rcsa-filter-label" htmlFor="rcsa-filter-year">
                        {t('RCSA.year', { defaultValue: 'Year' })}
                      </label>
                      <Select
                        inputId="rcsa-filter-year"
                        classNamePrefix="rcsa-select"
                        options={yearOptions}
                        styles={customSelectStyles}
                        placeholder={String(t('RCSA.year', { defaultValue: 'Year' }))}
                        value={yearOptions.find((option: any) => option.value === draftYear)}
                        onChange={(option: any) => setDraftYear(option?.value ?? null)}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        isRtl={isRTL}
                      />
                    </div>
                    <div className="rcsa-filter-field">
                      <label className="rcsa-filter-label" htmlFor="rcsa-filter-standard">
                        {t('RCSA.standard', { defaultValue: 'Standard' })}
                      </label>
                      <Select
                        inputId="rcsa-filter-standard"
                        classNamePrefix="rcsa-select"
                        options={[{ value: '', label: t('All Standard') || 'All Standard' }, ...(controlReferences || []).map((cr: any) => ({ value: cr.id, label: cr.name }))]}
                        styles={customSelectStyles}
                        placeholder={t('Filter by Control Reference') || 'Filter by Control Reference'}
                        value={(() => {
                          if (!draftStandardId) return { value: '', label: t('All Standards') || 'All Standards' };
                          const found = (controlReferences || []).find((cr: any) => cr.id === draftStandardId);
                          return { value: draftStandardId, label: found?.name || draftStandardId };
                        })()}
                        onChange={(option: any) => setDraftStandardId(option?.value || null)}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        isRtl={isRTL}
                      />
                    </div>
                  </div>
                </div>
                <div className="controls-catalog-popup-footer rcsa-filter-footer">
                  <button type="button" className="btn rcsa-filter-btn rcsa-filter-btn--reset" onClick={resetFilterDraft}>
                    {t('controls.reset', { defaultValue: 'Reset' })}
                  </button>
                  <button type="button" className="btn rcsa-filter-btn rcsa-filter-btn--apply" onClick={applyFilterPanel}>
                    {t('common.apply', { defaultValue: 'Apply' })}
                  </button>
                </div>
              </div>
            )}
          </>,
          document.body,
        )}

        <div className="services-catalog-table-card card border-0 shadow-sm overflow-hidden mb-0 rcsa-table-card">
          {filteredData.length > 0 ? (
            <div className="rcsa-table-scroll" dir={isRTL ? 'rtl' : 'ltr'}>
              <table
                className={`rcsa-table${isRTL ? ' rcsa-table--rtl' : ''}`}
                style={{ width: rcsaColumns.reduce((sum, col) => sum + col.width, 0), minWidth: '100%' }}
              >
                <colgroup>
                  {rcsaColumns.map((col) => (
                    <col key={col.key} style={{ width: col.width }} />
                  ))}
                </colgroup>
                <thead>
                  <tr>
                    {rcsaColumns.map((col) => (
                      <th key={col.key} scope="col" className={col.className} title={col.label}>
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((risk: any, index: number) => {
                    const rowKey: number = risk.index;
                    const expanded = expandedRows.has(rowKey);
                    const responseTone = getResponseTone(risk.bankResponse);
                    return (
                      <tr key={rowKey ?? index} className={expanded ? 'is-expanded' : undefined}>
                        <td className="rcsa-col-index">{risk.index}</td>
                        <td>{renderClampCell(risk.function, rowKey, 2, 18)}</td>
                        <td>{renderClampCell(risk.domain, rowKey, 2, 21)}</td>
                        <td className="rcsa-col-strong">{renderClampCell(risk.question, rowKey, 3, 41)}</td>
                        <td className="rcsa-col-response">
                          <div className="rcsa-response">
                            <span
                              className={`rcsa-status rcsa-status--${responseTone}`}
                              title={isEmptyValue(risk.bankResponse) ? 'N/A' : risk.bankResponse}
                            >
                              {isEmptyValue(risk.bankResponse) ? 'N/A' : risk.bankResponse}
                            </span>
                            {canUpdate && (
                              <button
                                type="button"
                                className="btn btn-sm data-mgmt-row-action data-mgmt-row-action--neutral surveys-page__catalog-icon-btn rcsa-edit-btn"
                                onClick={() => openEditModal(risk)}
                                title="Edit Bank Response"
                                aria-label="Edit Bank Response"
                              >
                                <MdOutlineEdit size={15} aria-hidden />
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="rcsa-col-type">
                          <span className={`rcsa-badge rcsa-badge--soft ${
                            risk.controlType === 'Mandatory' ? 'rcsa-badge--mandatory'
                              : risk.controlType === 'Advisory' ? 'rcsa-badge--advisory'
                              : 'rcsa-badge--na'
                          }`}>
                            {risk.controlType}
                          </span>
                        </td>
                        <td>{renderClampCell(risk.examples, rowKey, 3, 38)}</td>
                        <td>{renderClickableControls(risk)}</td>
                        <td>{renderClampCell(risk.complianceBrief, rowKey, 3, 41)}</td>
                        <td>{renderClampCell(risk.impactedArea, rowKey, 2, 21)}</td>
                        <td>{renderClampCell(risk.owner, rowKey, 2, 18)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="rcsa-empty-state" role="status">
              <div className="rcsa-empty-state__title">{t("No data available.")}</div>
              <p className="rcsa-empty-state__text">
                {t('RCSA.noDataForPeriod', {
                  period: `${periodLabel} · ${standardLabel}`,
                  defaultValue: 'No data for {{period}} — try another quarter, year or standard from Filters.',
                })}
              </p>
              <button type="button" className="rcsa-showing-change" onClick={openFilterPanel}>
                {t('RCSA.openFilters', { defaultValue: 'Open Filters' })}
              </button>
            </div>
          )}
        </div>

        {data.length > 0 && totalPages > 0 && (
          <div className="services-catalog-pagination-bottom d-flex justify-content-center pt-3 mt-2 rcsa-pagination">
            <DataMgmtCatalogPagination
              page={page + 1}
              totalPages={totalPages}
              totalFiltered={totalCount || data.length}
              busy={loading || searchLoading}
              onPageChange={(p) => handlePagination(p - 1)}
            />
          </div>
        )}
      </div>}

      {notAllowed && <div className="container_info">
        <div className="d-flex justify-content-between my-2 text-center alert alert-danger">
          <h5 style={{ width: '100%' }} className="text-center">{t("permissions.not_allowed")}</h5>
        </div>
      </div>}

      {/* Edit Bank Response Modal */}
      <Modal isOpen={isEditModalOpen} toggle={closeEditModal} size="md">
        <div className={`modal-content rcsa-modal ${isUpdating ? 'updating' : ''}`}>
          <div className="rcsa-modal-header">
            <h5 className="rcsa-modal-title">Edit Bank Response</h5>
            <button
              type="button"
              className="btn-close"
              onClick={closeEditModal}
              aria-label="Close"
            ></button>
          </div>
          <div className="rcsa-modal-body">
            {editingItem && (
              <div>
                <div className="rcsa-modal-section">
                  <span className="rcsa-modal-label">Question</span>
                  <div className="rcsa-modal-readonly-box">{editingItem.question}</div>
                </div>
                <div className="rcsa-modal-section">
                  <span className="rcsa-modal-label">Current Response</span>
                  <span className="rcsa-modal-current-pill">{editingItem.bankResponse}</span>
                </div>
                <div className="rcsa-modal-section mb-0">
                  <span className="rcsa-modal-label">Select New Response</span>
                  <div className="rcsa-choice-grid">
                    {bankResponseChoices.map((choice, index) => (
                      <label
                        key={index}
                        htmlFor={`choice-${index}`}
                        className={`rcsa-choice-card${selectedResponse === choice ? ' rcsa-choice-card--active' : ''}`}
                      >
                        <input
                          type="radio"
                          name="bankResponse"
                          id={`choice-${index}`}
                          value={choice}
                          checked={selectedResponse === choice}
                          onChange={() => handleResponseChange(choice)}
                        />
                        <span>{choice}</span>
                      </label>
                    ))}
                    {bankResponseChoices.length === 0 && (
                      <span className="text-muted small">Loading choices…</span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="rcsa-modal-footer">
            <button
              type="button"
              className="btn rcsa-modal-btn-cancel"
              onClick={closeEditModal}
              disabled={isUpdating}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn rcsa-modal-btn-primary"
              onClick={handleUpdateResponse}
              disabled={!selectedResponse || isUpdating || !canUpdate}
              title={!canUpdate ? "You don't have permission to update bank responses" : ""}
            >
              {isUpdating ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Updating...
                </>
              ) : (
                "Update Response"
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* View Full Content Modal */}
      <Modal isOpen={isViewModalOpen} toggle={closeViewModal} size="lg">
        <div className="modal-content rcsa-modal">
          <div className="rcsa-modal-header">
            <h5 className="rcsa-modal-title">{viewModalTitle}</h5>
            <button
              type="button"
              className="btn-close"
              onClick={closeViewModal}
              aria-label="Close"
            ></button>
          </div>
          <div className="rcsa-modal-body">
            <div className="rcsa-modal-readonly-box rcsa-modal-readonly-box--scroll">
              {viewModalContent || 'N/A'}
            </div>
          </div>
          <div className="rcsa-modal-footer">
            <button type="button" className="btn rcsa-modal-btn-cancel" onClick={closeViewModal}>
              {t('Close') || 'Close'}
            </button>
            <button type="button" className="btn rcsa-modal-btn-primary" onClick={copyViewContent}>
              {t('Copy') || 'Copy'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}